/**
 * When the monthly trainer review goes out (trainer-review-lane, 2026-10-23;
 * migration 249), and when its review call can be offered
 * (trainer-review-call-time, 2026-10-24; migration 250).
 *
 * Mario (23 Oct 2026): "sent automatically on the 3rd at 09:00 studio time,
 * configurable on /config". A studio stores its choice in the optional
 * `trainer_reviews` block of its L2 config (`l2-yaml.ts`, a live
 * `studio_config` field, so a Save applies from the lane's next tick). The
 * block ships `enabled: false`; Mario switches it on.
 *
 * The call (Mario, 24 Oct 2026): once a trainer has answered the four
 * questions they pick a time for the review call from the studio's review
 * windows — by default Tuesdays and Thursdays 12:00–14:00 on the 5th–14th
 * of the month the review goes out, 30-minute calls.
 *
 * This module holds the defaults, the ranges and the resolver, so the lane
 * that sends (`trainer_review_sender`), the router that offers the times,
 * the validator that parses the block and the /config form agree by
 * construction.
 *
 * Dependency-free leaf: imported by the SPA, the dashboard server and the
 * saga runtime alike, so it must stay free of Node and package imports.
 */

/** A studio's resolved schedule — every field always present. */
export interface TrainerReviewSchedule {
  /** Whether the lane sends this studio's reviews at all. */
  readonly enabled: boolean;
  /** Day of the month (1–28, so every month has it). */
  readonly send_day: number;
  /** Studio-local hour (0–23). */
  readonly send_hour: number;
  /** Whether a trainer who has answered the four questions is offered call times. */
  readonly call_times: boolean;
  /** ISO weekdays calls can be offered on: 1 = Monday … 7 = Sunday, ascending. */
  readonly call_weekdays: ReadonlyArray<number>;
  /** Studio-local `HH:MM` the window opens. */
  readonly call_from: string;
  /** Studio-local `HH:MM` the window closes; a call ends by then. */
  readonly call_to: string;
  /** First day of the send month calls can fall on (1–31). */
  readonly call_first_day: number;
  /** Last day of the send month calls can fall on (1–31; past the month's end means its last day). */
  readonly call_last_day: number;
  /** A call's length, and the step between offered starts. */
  readonly call_minutes: number;
}

/**
 * Off until a studio switches it on; then the 3rd at 09:00, with calls offered
 * on Tuesdays and Thursdays 12:00–14:00 on the 5th–14th, 30 minutes each.
 */
export const TRAINER_REVIEW_SCHEDULE_DEFAULTS: TrainerReviewSchedule = {
  enabled: false,
  send_day: 3,
  send_hour: 9,
  call_times: true,
  call_weekdays: [2, 4],
  call_from: "12:00",
  call_to: "14:00",
  call_first_day: 5,
  call_last_day: 14,
  call_minutes: 30,
};

export const TRAINER_REVIEW_SEND_DAY_MIN = 1;
/** 28, so the day exists in February too. */
export const TRAINER_REVIEW_SEND_DAY_MAX = 28;
export const TRAINER_REVIEW_SEND_HOUR_MIN = 0;
export const TRAINER_REVIEW_SEND_HOUR_MAX = 23;

export const TRAINER_REVIEW_CALL_DAY_MIN = 1;
export const TRAINER_REVIEW_CALL_DAY_MAX = 31;
/** The call lengths /config offers; the validator accepts exactly these. */
export const TRAINER_REVIEW_CALL_MINUTES_CHOICES: ReadonlyArray<number> = [15, 20, 30, 45, 60];

/** `HH:MM`, 00:00–23:59. */
export const TRAINER_REVIEW_CALL_TIME_PATTERN = "^([01][0-9]|2[0-3]):[0-5][0-9]$";
const CALL_TIME_RE = new RegExp(TRAINER_REVIEW_CALL_TIME_PATTERN);

/** Minutes since midnight of a valid `HH:MM`, or null. */
export function trainerReviewCallClockMinutes(hhmm: string): number | null {
  if (!CALL_TIME_RE.test(hhmm)) return null;
  return Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
}

/** Monday first, as the ISO numbers 1–7 the block stores. */
export const TRAINER_REVIEW_CALL_WEEKDAYS: ReadonlyArray<{
  readonly iso: number;
  readonly short: string;
}> = [
  { iso: 1, short: "Mon" },
  { iso: 2, short: "Tue" },
  { iso: 3, short: "Wed" },
  { iso: 4, short: "Thu" },
  { iso: 5, short: "Fri" },
  { iso: 6, short: "Sat" },
  { iso: 7, short: "Sun" },
];

/** A stored block, as the validator emits it: every field optional. */
export interface TrainerReviewScheduleBlock {
  readonly enabled?: boolean | undefined;
  readonly send_day?: number | undefined;
  readonly send_hour?: number | undefined;
  readonly call_times?: boolean | undefined;
  readonly call_weekdays?: ReadonlyArray<number> | undefined;
  readonly call_from?: string | undefined;
  readonly call_to?: string | undefined;
  readonly call_first_day?: number | undefined;
  readonly call_last_day?: number | undefined;
  readonly call_minutes?: number | undefined;
}

/**
 * Fill a stored (possibly partial or absent) block with the defaults, field
 * by field. The block is validated where it is parsed (`l2-yaml.ts`); this
 * only resolves what is missing.
 */
export function resolveTrainerReviewSchedule(
  block: TrainerReviewScheduleBlock | undefined,
): TrainerReviewSchedule {
  const d = TRAINER_REVIEW_SCHEDULE_DEFAULTS;
  return {
    enabled: block?.enabled ?? d.enabled,
    send_day: block?.send_day ?? d.send_day,
    send_hour: block?.send_hour ?? d.send_hour,
    call_times: block?.call_times ?? d.call_times,
    call_weekdays: block?.call_weekdays ?? d.call_weekdays,
    call_from: block?.call_from ?? d.call_from,
    call_to: block?.call_to ?? d.call_to,
    call_first_day: block?.call_first_day ?? d.call_first_day,
    call_last_day: block?.call_last_day ?? d.call_last_day,
    call_minutes: block?.call_minutes ?? d.call_minutes,
  };
}

/**
 * Whether a resolved schedule can offer any call at all: at least one
 * weekday, a window long enough for one call, and a first day no later than
 * the last. Each layer is validated on its own, so a live override and the
 * git YAML can still combine into an empty window (`call_from` from one,
 * `call_to` from the other); the lane and the router then offer nothing and
 * say so, rather than guess.
 */
export function trainerReviewCallWindowUsable(schedule: TrainerReviewSchedule): boolean {
  const from = trainerReviewCallClockMinutes(schedule.call_from);
  const to = trainerReviewCallClockMinutes(schedule.call_to);
  return (
    schedule.call_weekdays.length > 0 &&
    from !== null &&
    to !== null &&
    to - from >= schedule.call_minutes &&
    schedule.call_first_day <= schedule.call_last_day
  );
}
